// src/components/MonthlyItemCostTable.tsx
import React, { useEffect, useMemo, useRef } from "react";
import { EditableRow } from "../types/EditableRow";

type MonthDef = { ym: string; label: string };

function buildNext12MonthsFromNow(): MonthDef[] {
    const out: MonthDef[] = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 0; i < 12; i++) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        out.push({ ym: `${y}-${m}`, label: `${y}/${m}` });
        d.setMonth(d.getMonth() + 1);
    }
    return out;
}

export function MonthlyItemCostTable({
    rows,
    months,
    onChange,
    onDelete,
    avgCostRatesView,
    onChangeAvgCostRate,
    hideItemRows = false,

    // 列見出し（Amazon版は ASIN / SKU に差し替える。既定は楽天の従来表記）
    itemCodeHeader = "商品管理番号",
    skuCodeHeader = "SKU管理番号",

    // 商品名の列（表示専用）を出すか。楽天のみ true。Amazon/Yahoo は従来どおり出さない
    showItemName = false,

    // ✅ 追加：選択
    selectable = false,
    selectedKeys,
    onToggleSelect,
    onToggleSelectAll,
}: {
    rows: EditableRow[];
    months?: MonthDef[];
    onChange: (id: string, field: keyof EditableRow, value: any) => void;
    onDelete?: (row: EditableRow) => void;

    avgCostRatesView?: string[];
    onChangeAvgCostRate?: (idx: number, value: string) => void;

    hideItemRows?: boolean;

    itemCodeHeader?: string;
    skuCodeHeader?: string;

    showItemName?: boolean;

    // ✅ 追加
    selectable?: boolean;
    selectedKeys?: Set<string>;
    onToggleSelect?: (key: string, checked: boolean) => void;
    onToggleSelectAll?: (checked: boolean) => void;
}) {
    const monthCols: MonthDef[] =
        Array.isArray(months) && months.length === 12 ? months : buildNext12MonthsFromNow();

    // ✅ 選択可能なキー（表示中だけ）
    const selectableRowKeys = useMemo(() => {
        if (!selectable) return [];
        return (rows ?? [])
            .filter((r: any) => !r?.isNew && r?.item_key !== "__new__")
            .map((r: any) => String(r.old_item_key || r.item_key))
            .filter((k) => k && k !== "__new__");
    }, [rows, selectable]);

    const selectedSet = selectedKeys ?? new Set<string>();
    const allChecked =
        selectableRowKeys.length > 0 && selectableRowKeys.every((k) => selectedSet.has(k));
    const someChecked =
        selectableRowKeys.some((k) => selectedSet.has(k)) && !allChecked;

    // ✅ indeterminate
    const headCbRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (!headCbRef.current) return;
        headCbRef.current.indeterminate = selectable && someChecked;
    }, [selectable, someChecked]);

    // ★固定列幅
    const CHECK_W = 44;
    const STICKY_W = 120;

    const stickyLeft = (stickyIndex: number) => {
        // stickyIndex: 0=checkbox, 1=item_code, 2=sku_code, 3=item_name（showItemName のときだけ）
        if (selectable) {
            if (stickyIndex === 0) return 0;
            return CHECK_W + (stickyIndex - 1) * STICKY_W;
        }
        // selectable=false のときは従来の 4だけ
        return stickyIndex * STICKY_W;
    };

    const stickyStyle = (stickyIndex: number): React.CSSProperties => ({
        position: "sticky",
        left: stickyLeft(stickyIndex),
        transform: "translateZ(0)",
        willChange: "transform",
    });

    const stickyCellBase = `min-w-[120px] max-w-[120px] w-[120px]`;
    const stickyCheckBase = `min-w-[44px] max-w-[44px] w-[44px]`;
    // 商品名の列（stickyIndex 3。SKU の右隣に固定。幅だけ広い）
    const stickyNameBase = `min-w-[200px] max-w-[200px] w-[200px]`;

    // 商品管理番号を書き換えた行は、前の番号の名前を出さない（保存して読み直すと新しい名前になる）
    const itemNameOf = (row: EditableRow): string => {
        const name = String(row.item_name ?? "");
        if (!name) return "";
        const code = String(row.item_code ?? "").trim().toUpperCase();
        return code === String(row.item_name_code ?? "") ? name : "";
    };

    const editable = (
        row: EditableRow,
        field: "item_code" | "sku_code"
    ) => (
        <input
            className="w-full border px-1 py-0.5 text-xs"
            value={(row as any)[field] ?? ""}
            onChange={(e) => onChange((row as any).client_row_id || (row as any).item_key, field as any, e.target.value)}
        />
    );

    const showAvgRow = Array.isArray(avgCostRatesView) && avgCostRatesView.length === 12;

    return (
        <div className="overflow-x-auto relative">
            <table className="min-w-max border-separate border-spacing-0 text-xs">
                <thead>
                    <tr className="bg-gray-100">
                        {/* ✅ チェック列 */}
                        {selectable && (
                            <th
                                className={`border px-1 py-1 bg-gray-100 z-30 text-center align-middle ${stickyCheckBase}`}
                                style={stickyStyle(0)}
                            >
                                <input
                                    ref={headCbRef}
                                    type="checkbox"
                                    className="block mx-auto"
                                    checked={allChecked}
                                    onChange={(e) => onToggleSelectAll?.(e.target.checked)}
                                />
                            </th>
                        )}

                        {/* 先頭2列 sticky */}
                        <th
                            className={`border px-1 py-1 whitespace-nowrap bg-gray-100 z-30 ${stickyCellBase}`}
                            style={stickyStyle(selectable ? 1 : 0)}
                        >
                            {itemCodeHeader}
                        </th>
                        <th
                            className={`border px-1 py-1 whitespace-nowrap bg-gray-100 z-30 ${stickyCellBase}`}
                            style={stickyStyle(selectable ? 2 : 1)}
                        >
                            {skuCodeHeader}
                        </th>
                        {showItemName && (
                            <th
                                className={`border px-1 py-1 whitespace-nowrap bg-gray-100 z-30 ${stickyNameBase}`}
                                style={stickyStyle(selectable ? 3 : 2)}
                            >
                                商品名
                            </th>
                        )}

                        {monthCols.map((m) => (
                            <th key={m.ym} className="border px-1 py-1 whitespace-nowrap text-right bg-gray-100">
                                {m.label}
                                <div className="text-[10px] text-gray-500 leading-none">税込</div>
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {showAvgRow && (
                        <tr className="bg-slate-50">
                            {selectable && (
                                <td
                                    className={`border px-1 py-1 z-20 bg-slate-50 ${stickyCheckBase}`}
                                    style={stickyStyle(0)}
                                />
                            )}

                            <td
                                className={`border px-1 py-1 z-20 bg-slate-50 ${stickyCellBase}`}
                                style={stickyStyle(selectable ? 1 : 0)}
                            >
                                <div className="font-semibold text-slate-700">平均商品原価率（%）</div>
                            </td>
                            <td className={`border px-1 py-1 z-20 bg-slate-50 ${stickyCellBase}`} style={stickyStyle(selectable ? 2 : 1)} />
                            {showItemName && (
                                <td className={`border px-1 py-1 z-20 bg-slate-50 ${stickyNameBase}`} style={stickyStyle(selectable ? 3 : 2)} />
                            )}

                            {monthCols.map((m, idx) => (
                                <td key={m.ym} className="border px-1 py-1 whitespace-nowrap">
                                    <div className="flex items-center justify-end gap-1">
                                        <input
                                            type="number"
                                            step="0.1"
                                            inputMode="decimal"
                                            className="w-16 border px-1 py-0.5 text-xs text-right"
                                            value={avgCostRatesView?.[idx] ?? ""}
                                            onChange={(e) => onChangeAvgCostRate?.(idx, e.target.value)}
                                            onWheel={(e) => e.currentTarget.blur()}
                                            disabled={!onChangeAvgCostRate}
                                        />
                                        <span className="text-[10px] text-slate-500">%</span>
                                    </div>
                                </td>
                            ))}
                        </tr>
                    )}

                    {!hideItemRows &&
                        rows.map((row: any) => {
                            const rowKey = String(row.old_item_key || row.item_key);
                            const isSelectableRow = selectable && !row.isNew && row.item_key !== "__new__";

                            const arrRaw = row.monthly_costs;
                            const monthlyCosts: (number | null)[] = Array.isArray(arrRaw) ? arrRaw : Array(12).fill(null);

                            const setMonthlyCost = (idx: number, v: number | null) => {
                                const next = monthlyCosts.slice();
                                next[idx] = v;
                                onChange((row as any).client_row_id || row.item_key, "monthly_costs" as any, next);
                            };

                            const rowBg = row.isNew ? "bg-yellow-50" : "bg-white";

                            return (
                                <tr key={(row as any).client_row_id || row.item_key} className={rowBg}>
                                    {selectable && (
                                        <td
                                            className={`border px-1 py-0.5 z-20 ${rowBg} text-center align-middle ${stickyCheckBase}`}
                                            style={stickyStyle(0)}
                                        >
                                            <input
                                                type="checkbox"
                                                className="block mx-auto"
                                                disabled={!isSelectableRow}
                                                checked={isSelectableRow ? selectedSet.has(rowKey) : false}
                                                onChange={(e) => onToggleSelect?.(rowKey, e.target.checked)}
                                            />
                                        </td>
                                    )}

                                    <td className={`border px-1 py-0.5 z-20 ${rowBg} ${stickyCellBase}`} style={stickyStyle(selectable ? 1 : 0)}>
                                        {editable(row, "item_code")}
                                    </td>
                                    <td className={`border px-1 py-0.5 z-20 ${rowBg} ${stickyCellBase}`} style={stickyStyle(selectable ? 2 : 1)}>
                                        {editable(row, "sku_code")}
                                    </td>
                                    {showItemName && (
                                        <td
                                            className={`border px-1 py-0.5 z-20 ${rowBg} ${stickyNameBase}`}
                                            style={stickyStyle(selectable ? 3 : 2)}
                                            title={itemNameOf(row) || undefined}
                                        >
                                            <div className="truncate text-gray-700">{itemNameOf(row)}</div>
                                        </td>
                                    )}

                                    {monthCols.map((m, idx) => (
                                        <td key={m.ym} className="border px-1 py-0.5 whitespace-nowrap">
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                className="w-20 border px-1 py-0.5 text-xs text-right"
                                                value={monthlyCosts[idx] ?? ""}
                                                onChange={(e) => setMonthlyCost(idx, e.target.value === "" ? null : Number(e.target.value))}
                                                onWheel={(e) => e.currentTarget.blur()}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                </tbody>
            </table>
        </div>
    );
}